import React, { useEffect, useState } from 'react';
import { Newspaper, Building2, FileText, AlertCircle, Search, Filter, Clock, ChevronDown } from 'lucide-react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';

interface Actualite {
    id: number;
    type_actualite: string;
    date_bulletin: string;
    emetteur: string;
    numero: string | null;
    nature: string | null;
    objet: string | null;
    created_at: string | null;
}

const ActualitesScreen: React.FC = () => {
    const { token } = useAuth();
    const [actualites, setActualites] = useState<Actualite[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedId, setExpandedId] = useState<number | null>(null);

    useEffect(() => {
        if (token) {
            fetchActualites();
        }
    }, [token, filter]);

    const fetchActualites = async () => {
        try {
            setLoading(true);
            const typeParam = filter !== 'all' ? `?type_actualite=${filter}&limit=50` : '?limit=50';
            const response = await fetch(`${API_URL}/v1/actualites${typeParam}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setActualites(data);
            }
        } catch (error) {
            console.error('Error fetching actualites:', error);
        } finally {
            setLoading(false);
        }
    };

    const getTypeConfig = (type: string): {
        icon: React.ReactNode;
        color: string;
        bg: string;
        border: string;
        label: string;
    } => {
        switch (type.toUpperCase()) {
            case 'OPERATION':
                return {
                    icon: <Building2 size={18} />,
                    color: '#2563eb',
                    bg: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                    border: '#bfdbfe',
                    label: 'Opération',
                };
            case 'AVIS':
                return {
                    icon: <AlertCircle size={18} />,
                    color: '#d97706',
                    bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                    border: '#fcd34d',
                    label: 'Avis',
                };
            case 'COMMUNIQUE':
                return {
                    icon: <FileText size={18} />,
                    color: '#059669',
                    bg: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    border: '#a7f3d0',
                    label: 'Communiqué',
                };
            default:
                return {
                    icon: <Newspaper size={18} />,
                    color: '#64748b',
                    bg: '#f8fafc',
                    border: '#e2e8f0',
                    label: type,
                };
        }
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    };

    const filteredActualites = actualites.filter((actu) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            actu.emetteur.toLowerCase().includes(term) ||
            actu.nature?.toLowerCase().includes(term) ||
            actu.objet?.toLowerCase().includes(term)
        );
    });

    const counts = {
        all: actualites.length,
        OPERATION: actualites.filter((a) => a.type_actualite === 'OPERATION').length,
        AVIS: actualites.filter((a) => a.type_actualite === 'AVIS').length,
        COMMUNIQUE: actualites.filter((a) => a.type_actualite === 'COMMUNIQUE').length,
    };

    return (
        <SiteLayout>
            <Header icon='article' title='Actualités BRVM' />
            <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>

                {/* Stats Row */}
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '16px',
                        marginBottom: '24px',
                    }}
                >
                    {[
                        { type: 'OPERATION', label: 'Opérations', icon: <Building2 size={24} />, gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' },
                        { type: 'AVIS', label: 'Avis', icon: <AlertCircle size={24} />, gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' },
                        { type: 'COMMUNIQUE', label: 'Communiqués', icon: <FileText size={24} />, gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
                    ].map((stat) => (
                        <div
                            key={stat.type}
                            onClick={() => setFilter(filter === stat.type ? 'all' : stat.type)}
                            style={{
                                background: filter === stat.type ? stat.gradient : 'white',
                                borderRadius: '16px',
                                padding: '20px',
                                cursor: 'pointer',
                                border: filter === stat.type ? 'none' : '1px solid #e2e8f0',
                                transition: 'all 0.2s',
                                color: filter === stat.type ? 'white' : '#1e293b',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ fontSize: '13px', opacity: 0.8, marginBottom: '4px' }}>{stat.label}</div>
                                    <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
                                        {counts[stat.type as keyof typeof counts]}
                                    </div>
                                </div>
                                <div style={{ opacity: 0.8 }}>{stat.icon}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Search Bar */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '24px',
                        padding: '12px 16px',
                        background: 'var(--bg-box)',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                    }}
                >
                    <Search size={20} color='#94a3b8' />
                    <input
                        type='text'
                        placeholder='Rechercher par émetteur, nature ou objet...'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#1e293b',
                        }}
                    />
                    {filter !== 'all' && (
                        <button
                            onClick={() => setFilter('all')}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '20px',
                                border: '1px solid #e2e8f0',
                                background: '#f1f5f9',
                                color: '#64748b',
                                fontSize: '12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                            }}
                        >
                            <Filter size={12} /> {filter} ✕
                        </button>
                    )}
                </div>

                {/* News List */}
                {loading ? (
                    <div
                        style={{
                            textAlign: 'center',
                            padding: '60px',
                            color: '#94a3b8',
                        }}
                    >
                        <Newspaper size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                        <div>Chargement des actualités...</div>
                    </div>
                ) : filteredActualites.length === 0 ? (
                    <div
                        style={{
                            textAlign: 'center',
                            padding: '60px',
                            color: '#94a3b8',
                            background: 'var(--bg-box)',
                            borderRadius: '16px',
                        }}
                    >
                        <Newspaper size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                        <div>Aucune actualité trouvée</div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {filteredActualites.map((actu) => {
                            const config = getTypeConfig(actu.type_actualite);
                            const isExpanded = expandedId === actu.id;

                            return (
                                <div
                                    key={actu.id}
                                    style={{
                                        background: 'var(--bg-box)',
                                        borderRadius: '12px',
                                        border: '1px solid #e2e8f0',
                                        overflow: 'hidden',
                                        transition: 'box-shadow 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                >
                                    {/* Card Header */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '16px',
                                            gap: '16px',
                                            cursor: actu.objet ? 'pointer' : 'default',
                                        }}
                                        onClick={() => actu.objet && setExpandedId(isExpanded ? null : actu.id)}
                                    >
                                        {/* Type Icon */}
                                        <div
                                            style={{
                                                minWidth: '44px',
                                                height: '44px',
                                                borderRadius: '12px',
                                                background: config.bg,
                                                border: `1px solid ${config.border}`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: config.color,
                                            }}
                                        >
                                            {config.icon}
                                        </div>

                                        {/* Content */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                <span style={{ fontWeight: '600', color: '#1e293b' }}>{actu.emetteur}</span>
                                                <span
                                                    style={{
                                                        fontSize: '11px',
                                                        color: config.color,
                                                        background: config.bg,
                                                        padding: '2px 8px',
                                                        borderRadius: '10px',
                                                        border: `1px solid ${config.border}`,
                                                    }}
                                                >
                                                    {config.label}
                                                </span>
                                            </div>
                                            {actu.nature && actu.nature.toLowerCase() !== actu.emetteur.toLowerCase() && (
                                                <div
                                                    style={{
                                                        fontSize: '13px',
                                                        color: '#64748b',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {actu.nature}
                                                </div>
                                            )}
                                        </div>

                                        {/* Date & Expand */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    fontSize: '12px',
                                                    color: '#94a3b8',
                                                }}
                                            >
                                                <Clock size={14} />
                                                {formatDate(actu.date_bulletin)}
                                            </div>
                                            {actu.objet && (
                                                <ChevronDown
                                                    size={20}
                                                    color='#94a3b8'
                                                    style={{
                                                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                                                        transition: 'transform 0.2s',
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {/* Expanded Content */}
                                    {isExpanded && actu.objet && (
                                        <div
                                            style={{
                                                padding: '0 16px 16px 76px',
                                                borderTop: '1px solid #f1f5f9',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    paddingTop: '16px',
                                                    fontSize: '14px',
                                                    color: '#475569',
                                                    lineHeight: '1.6',
                                                    whiteSpace: 'pre-wrap',
                                                }}
                                            >
                                                {actu.objet}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </SiteLayout>
    );
};

export default ActualitesScreen;

